// Tactics: the motifs of your own missed tactics and only moves, counted —
// what kind of thing keeps getting walked past, how much of it has since
// been found, and a way to train just that.

import { getJson } from "../app/api.ts";
import { fmtCount } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { DEFENCE_TAGS, TAG_MEANS, humanTag } from "../app/kinds.ts";
import { prefs, savePrefs } from "../app/prefs.ts";
import { navigate } from "../app/router.ts";
import { LichessLink, Pending, ScreenHead, useLoad } from "../app/ui.tsx";

interface Motif {
  tag: string;
  total: number;
  solved: number;
}

export function TacticsScreen() {
  const [motifs] = useLoad(() => getJson<{ motifs?: Motif[] }>("/api/motifs").then((d) => d.motifs || []));
  return (
    <div className="screen">
      <ScreenHead title="Tactics" subtitle="What you miss: your missed tactics and only moves, by what kind of thing they were. Most missed first." />
      {motifs.state !== "ready" ? (
        <Pending value={motifs} text="Counting what you miss…" />
      ) : !motifs.data.length ? (
        <div className="empty card">
          <h2>Nothing to count yet</h2>
          <p>
            This page counts what you miss — forks, pins, pieces left hanging — once some of your games have been read. Until then Lichess has every one of them to
            practise on.
          </p>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" onClick={() => navigate("/")}>
              Train
            </button>
            <a className="btn" href="https://lichess.org/training/themes" target="_blank" rel="noopener">
              <Icon name="ext" />
              Themes on Lichess
            </a>
          </div>
        </div>
      ) : (
        <>
          <Section
            title="Wins you walked past"
            sub="How the win came about, in the tactics and only moves you missed. One position can be several of these."
            list={motifs.data.filter((m) => !DEFENCE_TAGS.has(m.tag))}
          />
          <Section
            title="Defences you missed"
            sub="Positions where one move held. What was threatened, and what kind of answer it needed."
            list={motifs.data.filter((m) => DEFENCE_TAGS.has(m.tag))}
          />
        </>
      )}
    </div>
  );
}

function Section({ title, sub, list }: { title: string; sub: string; list: Motif[] }) {
  if (!list.length) return null;
  return (
    <section className="card motif-card">
      <div className="rep-list-head">
        <h2>{title}</h2>
      </div>
      <p className="help">{sub}</p>
      {list.map((m) => {
        const left = m.total - m.solved;
        return (
          <div key={m.tag} className="motif-row">
            <div className="motif-main">
              <div className="motif-name">{humanTag(m.tag)}</div>
              {TAG_MEANS[m.tag] ? <div className="motif-means">{TAG_MEANS[m.tag]}</div> : null}
              <div className="motif-bar" title={`${m.solved} of ${m.total} found since`}>
                <div className="motif-bar-done" style={{ width: `${m.total ? Math.round((100 * m.solved) / m.total) : 0}%` }} />
              </div>
            </div>
            <div className="motif-side">
              <div className="motif-count num">
                <b>{fmtCount(left)}</b> waiting
              </div>
              <div className="faint num">
                {fmtCount(m.total)} missed · {fmtCount(m.solved)} found since
              </div>
              <div className="motif-actions">
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={left === 0}
                  title={left ? "" : "You have found all of these. Lichess has more."}
                  onClick={() => {
                    prefs.trainMotif = m.tag;
                    // A motif cuts across the kinds: a fork is a tactic in one
                    // game and the only move in another.
                    prefs.trainKind = "";
                    savePrefs();
                    navigate("/");
                  }}
                >
                  <Icon name="target" />
                  Train these
                </button>
                {/* Your own run out; Lichess's do not. */}
                <LichessLink tag={m.tag} />
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
